import { useLazyQuery, useMutation } from "@apollo/client";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import LeakRemoveIcon from "@mui/icons-material/LeakRemove";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { useContext, useEffect } from "react";
import { Form, Spinner, Button } from "react-bootstrap";
import { UPDATE_ACTION_STATUS_MUTATION } from "../../backend/hooks/action/mutationUpdateActionStatus";
import { GET_DOWNLOAD_LINKS } from "../../backend/hooks/importExportMenu/queryGetDownloadLinks";
import { useGetFilePartialContentQuery } from "../../backend/hooks/pmxActivity/queryGetFilePartialContent";
import { ActionStatus } from "../../backend/types";
import { PmxActivityDetailsContext } from "../../contexts/pmxActivityDetailsProvider";
import Error from "../abstractComponents/error";
import { ComplexFilePreview } from "./complexFilePreview";
import { useHistory } from "react-router-dom";

interface FilePreviewProps {
  repoId: string;
  absolutePath: string;
  versionId: string;
  revision: string;
  openPropertiesModal: boolean;
  setTableMenuWasOpenOnPath: Function;
  setIsActionOfFilesVisible: Function;
  setIsPreselectedRevision: Function;
  setIsTracebilityReportVisible: Function;
}

// Once you select a file, the preview will show you the content of the file, if available. Supports text, and images
export function FilePreview(props: FilePreviewProps) {
  const { activityData } = useContext(PmxActivityDetailsContext);
  const history = useHistory();

  const isPropImage = () => {
    return (
      props.absolutePath.endsWith(".png") ||
      props.absolutePath.endsWith(".jpeg") ||
      props.absolutePath.endsWith(".jpg") ||
      props.absolutePath.endsWith(".gif")
    );
  };

  const isPdfFile = () => {
    return props.absolutePath.endsWith(".pdf");
  };

  const isHtmlFile = () => {
    return (
      props.absolutePath.endsWith(".html") ||
      props.absolutePath.endsWith(".xhtml")
    );
  };

  const unicodePreviewCheck = () => {
    if (!isPropImage()) {
      return fileDetails?.getFilePartialContent.isPreviewAvailable;
    }
  };

  const { fileDetails, isLoading, error, refetchFilePartialContent } =
    useGetFilePartialContentQuery(
      props.repoId,
      props.absolutePath,
      props.versionId
    );

  const handleDownloadFile = () => {
    getDownloadLinkForFiles({
      variables: {
        absolutePaths: [props.absolutePath],
        activityId: Number(activityData.id),
        versionIds: props.versionId ? [props.versionId] : [],
        repoId: props.repoId
      }
    });
  };

  useEffect(() => {
    refetchFilePartialContent();
  }, [props.repoId]);

  const [
    getDownloadLinkForFiles,
    { error: errorDownload, data: downloadUrls }
  ] = useLazyQuery(GET_DOWNLOAD_LINKS);

  const [updateActionStatus] = useMutation(UPDATE_ACTION_STATUS_MUTATION, {
    onError(err: any) {
      console.log(err);
    }
  });

  useEffect(() => {
    refetchFilePartialContent();
  }, [props.repoId]);

  useEffect(() => {
    if (typeof downloadUrls !== "undefined") {
      if (downloadUrls.getDownloadLinks.actionStatus === ActionStatus.Pending) {
        try {
          downloadUrls?.getDownloadLinks?.presignedUrls.forEach((url: any) => {
            window.open(url.url);
          });
          updateActionStatus({
            variables: {
              actionId: downloadUrls.getDownloadLinks.actionId,
              actionStatus: ActionStatus.Success
            }
          });
        } catch (err: any) {
          updateActionStatus({
            variables: {
              actionId: downloadUrls.getDownloadLinks.actionId,
              actionStatus: ActionStatus.Error,
              actionStatusDescription: err.message
            }
          });
        }
      }
    }
  }, [downloadUrls]);

  useEffect(() => {
    if (props.openPropertiesModal) {
      setTimeout(() => {
        props.setTableMenuWasOpenOnPath(props.absolutePath);
        props.setIsActionOfFilesVisible(true);
        props.setIsPreselectedRevision(true);
      }, 1500);
    }
  }, []);

  return (
    <>
      <div className="table_view_upper_menu text-break">
        <b>{props.absolutePath}</b>{" "}
        {props.revision && props.revision !== "undefined" && (
          <>({props.revision})</>
        )}
        <Button
          size="sm"
          variant="outline-dark"
          onClick={() => {
            props.setTableMenuWasOpenOnPath(props.absolutePath);
            props.setIsActionOfFilesVisible(true);
          }}
          className="float-end properties_file_button ms-3"
        >
          <DescriptionIcon /> Properties
        </Button>
        <Button
          size="sm"
          variant="outline-dark"
          onClick={handleDownloadFile}
          className="float-end download_file_button ms-3"
        >
          <CloudDownloadIcon /> Download
        </Button>
        <Button
          size="sm"
          variant="outline-dark"
          onClick={() => {
            props.setTableMenuWasOpenOnPath(props.absolutePath);
            props.setIsTracebilityReportVisible(true);
          }}
          className="float-end traceability_file_button ms-3"
        >
          <LeakRemoveIcon /> Tracebility
        </Button>
        {unicodePreviewCheck() && (
          <Button
            size="sm"
            variant="outline-dark"
            onClick={() => {
              history.push(
                `/activity/${activityData.id}/compare?sourceVersionId=${props.versionId}&sourceRepoId=${props.repoId}`
              );
            }}
            className="float-end compare_file_button ms-3"
          >
            <CompareArrowsIcon />
            Compare
          </Button>
        )}
      </div>
      <br />
      {isLoading ? (
        <div className="center_div">
          <Spinner animation="border" className="spinner_color" />
          <p>Loading...</p>
        </div>
      ) : error || errorDownload ? (
        <Error error={error || errorDownload} />
      ) : (
        <>
          {fileDetails?.getFilePartialContent.isPreviewAvailable ? (
            isPropImage() ? (
              <img
                className="preview_display_image"
                alt={"Image for " + props.absolutePath}
                src={fileDetails?.getFilePartialContent.content}
              />
            ) : isHtmlFile() ? (
              <iframe
                className="html_preview"
                srcDoc={fileDetails?.getFilePartialContent.content}
                width={1000}
                height={600}
              ></iframe>
            ) : (
              <>
                <p>Previewing first 100 lines</p>
                <Form.Control
                  id="token-textarea"
                  className=""
                  as="textarea"
                  disabled
                  rows={15}
                  value={
                    fileDetails
                      ? fileDetails?.getFilePartialContent.isTruncated
                        ? fileDetails?.getFilePartialContent.content +
                          "\n[...file truncated...]"
                        : fileDetails?.getFilePartialContent.content
                      : ""
                  }
                ></Form.Control>
              </>
            )
          ) : isPdfFile() || isHtmlFile() ? (
            <ComplexFilePreview
              absolutePath={props.absolutePath}
              versionId={props.versionId}
              repoId={props.repoId}
            />
          ) : (
            <p>No preview available</p>
          )}
        </>
      )}
    </>
  );
}
